import { resolveImageSrc } from './image'
import {
  SITE_NAME,
  SITE_SHARE_IMAGE,
  type SeoData,
  absoluteUrl,
  breadcrumbLd,
  collectionPageLd,
  publisherLd,
  stripHtmlToText,
  truncate,
} from './seo'

/** Thai game titles are rarely translated — keep the Latin name, add Thai intent. */
const titleOf = (value: any): string =>
  value && typeof value === 'object' ? value.title || value.name || '' : typeof value === 'string' ? value : ''

const titles = (list: any): string[] =>
  (Array.isArray(list) ? list : []).map(titleOf).filter(Boolean)

/**
 * Editorial score, 0–10. Prefers the newer single-score field and falls back to
 * averaging the four legacy sub-scores (the same math the Game component uses).
 */
const editorialScore = (game: any): number | null => {
  const direct = Number(game?.newReviewScore?.score)
  if (Number.isFinite(direct) && direct > 0 && direct <= 10) return Math.round(direct * 10) / 10

  const rs = game?.reviewScore
  const parts = [rs?.story, rs?.graphicAndSound, rs?.gameplay, rs?.performance].map(Number)
  if (parts.some((p) => !Number.isFinite(p))) return null
  const avg = parts.reduce((a, b) => a + b, 0) / parts.length
  return avg > 0 && avg <= 10 ? Math.round(avg * 10) / 10 : null
}

const isoDate = (value: any): string | undefined => {
  if (!value) return undefined
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? undefined : new Date(time).toISOString()
}

// ─── Game detail page ─────────────────────────────────────────────────────────
/**
 * SEO for /game/[id] — the per-game hub (info, editorial score, and that game's
 * news / features / guides). Every field is derived from the game document, so
 * this works for every game in the database rather than a hardcoded list.
 */
export const buildGameSeo = (opts: { gameId: string; game: any | null }): SeoData => {
  const game = opts.game
  const canonicalPath = `/game/${opts.gameId}`
  const name = titleOf(game)

  const genres = titles(game?.gameGenres)
  const platforms = titles(game?.platform)
  const developers = titles(game?.developers)
  const publishers = titles(game?.publishers)
  const synopsis = stripHtmlToText(game?.synopsis)
  const score = editorialScore(game)
  const released = isoDate(game?.releaseDate)
  // Only an explicit non-Published status counts as draft. Games saved before
  // the `status` field existed come back with it undefined, and those are real
  // live pages — treating "missing" as draft would deindex them.
  const isDraft = Boolean(game?.status) && game.status !== 'Published'

  // Thai players search "[Latin game name] + [Thai intent word]" — รีวิว, ข่าว,
  // ไกด์, วันวางจำหน่าย are the four highest-intent modifiers for a game hub.
  const title = `${name} — รีวิว ข่าว ไกด์ วันวางจำหน่าย | ${SITE_NAME}`

  const facts = [
    platforms.length ? `ลงเครื่อง ${platforms.slice(0, 4).join(' ')}` : '',
    genres.length ? `แนว ${genres.slice(0, 3).join(' ')}` : '',
    developers.length ? `พัฒนาโดย ${developers[0]}` : '',
    score !== null ? `คะแนนรีวิว ${score}/10` : '',
  ].filter(Boolean)

  const description = truncate(
    [
      `ข้อมูลเกม ${name} ครบทุกด้าน`,
      facts.join(' · '),
      synopsis,
      `รวมข่าว บทความ รีวิว และไกด์ ${name} ทั้งหมดจาก ${SITE_NAME}`,
    ]
      .filter(Boolean)
      .join(' '),
    300,
  )

  const keywords = [
    name,
    `${name} รีวิว`,
    `${name} ข่าว`,
    `${name} ไกด์`,
    `${name} วันวางจำหน่าย`,
    `${name} review`,
    ...genres.slice(0, 2).map((g) => `เกมแนว ${g}`),
    ...platforms.slice(0, 3).map((p) => `เกม ${p}`),
  ].filter(Boolean)

  const thumbnail = resolveImageSrc(game?.thumbnail)

  const videoGameLd: Record<string, any> = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name,
    url: absoluteUrl(canonicalPath),
    inLanguage: 'th',
    ...(synopsis ? { description: truncate(synopsis, 500) } : {}),
    ...(thumbnail ? { image: thumbnail } : {}),
    ...(released ? { datePublished: released } : {}),
    ...(genres.length ? { genre: genres } : {}),
    ...(platforms.length ? { gamePlatform: platforms } : {}),
    ...(developers.length
      ? { author: developers.map((t) => ({ '@type': 'Organization', name: t })) }
      : {}),
    ...(publishers.length
      ? { publisher: publishers.map((t) => ({ '@type': 'Organization', name: t })) }
      : {}),
    // Single editorial review by the site — never an aggregateRating, which
    // would require a real count of independent ratings we don't have here.
    ...(score !== null
      ? {
          review: {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: score,
              bestRating: 10,
              worstRating: 0,
            },
            author: { '@type': 'Organization', name: SITE_NAME },
            publisher: publisherLd,
          },
        }
      : {}),
  }

  return {
    title,
    description,
    keywords,
    canonicalPath,
    ogType: 'website',
    ogImage: thumbnail || SITE_SHARE_IMAGE,
    ogImageAlt: name ? `${name} — ${SITE_NAME}` : SITE_NAME,
    // A game page with no title is a broken shell. Draft games are served by
    // /games/:id just like published ones (the endpoint has no status filter),
    // so they're kept out of the index here rather than left to leak.
    noindex: !name || isDraft,
    jsonLd: name && !isDraft
      ? [
          videoGameLd,
          breadcrumbLd([
            { name: 'หน้าแรก', path: '/' },
            { name: 'เกมทั้งหมด', path: '/calendar' },
            { name },
          ]),
        ]
      : [],
  }
}

// ─── Per-game article listings (/game/features/[id], /game/guides/[id]) ───────
/**
 * `articleIds` is the first page of the listing, resolved server-side. It does
 * double duty: it becomes the CollectionPage's ItemList, and an empty array
 * marks the listing as having nothing to rank for, which is noindexed rather
 * than shipped as one of thousands of near-identical empty shells.
 */
export const buildGameArticleListSeo = (opts: {
  gameId: string
  game: any | null
  kind: 'features' | 'guides'
  articleIds?: string[]
}): SeoData => {
  const name = titleOf(opts.game)
  const canonicalPath = `/game/${opts.kind}/${opts.gameId}`
  const isGuides = opts.kind === 'guides'
  const label = isGuides ? 'ไกด์' : 'บทความ'

  const heading = name ? `${label} ${name}` : isGuides ? 'รวมไกด์เกม' : 'รวมบทความเกม'
  const title = `${heading}${name ? ' ทั้งหมด' : ''} | ${SITE_NAME}`

  const description = name
    ? isGuides
      ? `รวมไกด์ ${name} ทั้งหมด แนวทางการเล่น เทคนิค เคล็ดลับ และคู่มือสำหรับผู้เล่นใหม่จนถึงระดับสูง จาก ${SITE_NAME}`
      : `รวมบทความเกม ${name} ทั้งหมด บทวิเคราะห์ เจาะลึกเนื้อหา และเรื่องน่ารู้เกี่ยวกับเกม จาก ${SITE_NAME}`
    : `รวม${isGuides ? 'ไกด์' : 'บทความ'}เกมทั้งหมดของเกมนี้ อ่านแนวทางการเล่น เทคนิค และเคล็ดลับจาก ${SITE_NAME}`

  // `undefined` means "not resolved" (client-only render) — only an explicitly
  // empty array proves the listing has no articles.
  const isEmpty = Array.isArray(opts.articleIds) && opts.articleIds.length === 0
  const noindex = !name || isEmpty

  return {
    title,
    description,
    keywords: name
      ? [
          `${name} ${label}`,
          `${name} ${isGuides ? 'guide' : 'บทความ'}`,
          `${name} วิธีเล่น`,
          `ไกด์ ${name}`,
        ]
      : undefined,
    canonicalPath,
    ogType: 'website',
    ogImage: resolveImageSrc(opts.game?.thumbnail) || SITE_SHARE_IMAGE,
    ogImageAlt: name ? `${heading} — ${SITE_NAME}` : SITE_NAME,
    noindex,
    jsonLd: noindex
      ? []
      : [
          collectionPageLd({
            name: title,
            description,
            canonicalPath,
            itemUrls: opts.articleIds?.map((id) => `/article/${id}`),
          }),
          breadcrumbLd([
            { name: 'หน้าแรก', path: '/' },
            ...(name ? [{ name, path: `/game/${opts.gameId}` }] : []),
            { name: heading },
          ]),
        ],
  }
}

// ─── Tag / topic hub pages ────────────────────────────────────────────────────
export type TagKind =
  | 'platform'
  | 'genre'
  | 'developer'
  | 'publisher'
  | 'franchise'
  | 'highlight'
  | 'author'
  | 'category'
  | 'game'

/** Backend lookup path per tag type. `author`/`category` have no tag endpoint. */
export const TAG_ENDPOINTS: Partial<Record<TagKind, string>> = {
  platform: 'tag/platform',
  genre: 'tag/game-genre',
  developer: 'tag/developer',
  publisher: 'tag/publisher',
  franchise: 'tag/franchise',
  highlight: 'tag/highlight',
  game: 'games',
}

/**
 * Thai phrasing per facet. Each reads naturally as a search query rather than
 * a template — "เกมแนว Action" is how a Thai player searches a genre hub,
 * "เกมจาก Kuro Games" a developer hub.
 */
const TAG_PHRASING: Record<TagKind, (label: string) => { heading: string; intent: string }> = {
  platform: (l) => ({ heading: `เกม ${l}`, intent: `ข่าว รีวิว และบทความเกมบน ${l}` }),
  genre: (l) => ({ heading: `เกมแนว ${l}`, intent: `ข่าว รีวิว และบทความเกมแนว ${l}` }),
  developer: (l) => ({ heading: `เกมจาก ${l}`, intent: `ข่าว รีวิว และบทความเกมจากผู้พัฒนา ${l}` }),
  publisher: (l) => ({ heading: `เกมจาก ${l}`, intent: `ข่าว รีวิว และบทความเกมจากผู้จัดจำหน่าย ${l}` }),
  franchise: (l) => ({ heading: `ซีรีส์ ${l}`, intent: `ข่าว รีวิว และบทความทุกภาคในซีรีส์ ${l}` }),
  highlight: (l) => ({ heading: l, intent: `ข่าว รีวิว และบทความเกมในหัวข้อ ${l}` }),
  author: (l) => ({ heading: `บทความโดย ${l}`, intent: `ข่าว รีวิว และบทความเกมทั้งหมดโดย ${l}` }),
  category: (l) => ({ heading: l, intent: `ข่าว รีวิว และบทความเกมหมวด ${l}` }),
  game: (l) => ({ heading: `ข่าวและบทความ ${l}`, intent: `ข่าว รีวิว ไกด์ และบทความเกม ${l}` }),
}

/**
 * SEO for /tag/[id]. The route is query-driven (?type=&title=), so the canonical
 * keeps only `type` — the param that actually changes what's listed — and drops
 * the cosmetic `title`, which otherwise forks one hub into many duplicate URLs.
 * `label` is resolved server-side from the tag document so the indexed URL still
 * renders a real heading without the query param.
 */
export const buildTagSeo = (opts: {
  tagId: string
  type?: string
  label?: string
  synopsis?: string
}): SeoData => {
  const kind = (opts.type && opts.type in TAG_PHRASING ? opts.type : undefined) as TagKind | undefined
  const label = (opts.label || '').trim()

  // Without a facet type the page falls through to a free-text content search,
  // which is a search-results page and has no business in the index.
  if (!kind || !label) {
    return {
      title: `หัวข้อเกม | ${SITE_NAME}`,
      description: `รวมข่าว รีวิว และบทความเกมตามหัวข้อที่คุณสนใจ จาก ${SITE_NAME}`,
      canonicalPath: `/tag/${opts.tagId}${opts.type ? `?type=${encodeURIComponent(opts.type)}` : ''}`,
      ogType: 'website',
      ogImage: SITE_SHARE_IMAGE,
      ogImageAlt: SITE_NAME,
      noindex: true,
      jsonLd: [],
    }
  }

  const { heading, intent } = TAG_PHRASING[kind](label)
  const canonicalPath = `/tag/${opts.tagId}?type=${encodeURIComponent(kind)}`
  const synopsis = stripHtmlToText(opts.synopsis)
  const title = `${heading} — ข่าว รีวิว บทความ | ${SITE_NAME}`
  const description = truncate(
    [`รวม${intent} อัปเดตล่าสุดจาก ${SITE_NAME}`, synopsis].filter(Boolean).join(' '),
    300,
  )

  return {
    title,
    description,
    keywords: [heading, `${label} ข่าว`, `${label} รีวิว`, `${label} บทความ`, label],
    canonicalPath,
    ogType: 'website',
    ogImage: SITE_SHARE_IMAGE,
    ogImageAlt: `${heading} — ${SITE_NAME}`,
    jsonLd: [
      collectionPageLd({ name: title, description, canonicalPath }),
      breadcrumbLd([{ name: 'หน้าแรก', path: '/' }, { name: heading }]),
    ],
  }
}
